import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ChevronDown,
  Globe,
  Loader2,
  Plus,
  Save,
  Search,
  ShieldBan,
  X,
} from 'lucide-react'
import { SettingsUpdatedAt } from './SettingsUpdatedAt'
import { getCountries } from 'react-phone-number-input'
import countryLabels from 'react-phone-number-input/locale/en.json'
import Flags from 'react-phone-number-input/flags'
import { useListboxNav } from '../lib/use-listbox-nav'
import { api, extractApiError } from '../lib/api'

/** Toggle switch — same shape as the Maintenance / Payments pages so
 *  this surface feels consistent with the rest of the Super Admin area. */
function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ' +
        (checked
          ? 'bg-[color:var(--color-primary)]'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

/** Sorted, English-named ISO 3166-1 country list from the
 *  `react-phone-number-input` metadata (same source as ui/CountryFilter),
 *  computed once at module load. Codes are alpha-2 — the exact shape the
 *  backend `geo.blockedCountries` list stores. */
const ALL_COUNTRIES = (() => {
  const rows = getCountries().map((code) => ({
    code,
    name: countryLabels[code] ?? code,
  }))
  rows.sort((a, b) => a.name.localeCompare(b.name))
  return rows
})()
const COUNTRY_NAME = Object.fromEntries(ALL_COUNTRIES.map((c) => [c.code, c.name]))

/** Multi-select country picker for the Region lock block-list. Selected
 *  regions render as removable chips; "Add region" opens a searchable
 *  popover of the remaining countries. `value` / `onChange` are arrays of
 *  ISO alpha-2 codes. Mirrors the popover UX of ui/CountryFilter. */
function CountryMultiSelect({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef(null)
  const searchRef = useRef(null)
  const selectedSet = useMemo(() => new Set(value), [value])

  const available = useMemo(() => {
    const q = query.trim().toLowerCase()
    return ALL_COUNTRIES.filter(
      (c) =>
        !selectedSet.has(c.code) &&
        (!q ||
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q)),
    )
  }, [query, selectedSet])

  const { highlightedIndex, setHighlightedIndex, optionRefs, onKeyDown } =
    useListboxNav({
      open,
      count: available.length,
      onEnter: (i) => {
        const c = available[i]
        if (c) add(c.code)
      },
    })

  useEffect(() => {
    if (!open) return undefined
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    function onDocPointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
        setQuery('')
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function add(code) {
    if (!selectedSet.has(code)) onChange([...value, code])
    setQuery('')
    setHighlightedIndex(0)
    searchRef.current?.focus()
  }
  function remove(code) {
    onChange(value.filter((c) => c !== code))
  }

  return (
    <div>
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((code) => {
            const Flag = Flags[code]
            const name = COUNTRY_NAME[code] ?? code
            return (
              <span
                key={code}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] pl-2 pr-1 py-1 text-sm text-[color:var(--color-ink-strong)]"
              >
                {Flag ? (
                  <Flag title={name} style={{ width: 18, height: 12 }} />
                ) : null}
                {name}
                <button
                  type="button"
                  onClick={() => remove(code)}
                  disabled={disabled}
                  aria-label={`Remove ${name}`}
                  className="inline-flex items-center justify-center rounded-full p-0.5 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <X size={13} />
                </button>
              </span>
            )
          })}
        </div>
      ) : (
        <p className="mb-2 text-sm text-[color:var(--color-ink-subtle)]">
          No regions blocked yet.
        </p>
      )}

      <div ref={containerRef} className="relative inline-block">
        <button
          type="button"
          onClick={() => {
            if (!open) setHighlightedIndex(0)
            setOpen((v) => !v)
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] cursor-pointer hover:border-[color:var(--color-accent-border)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={14} />
          Add region
          <ChevronDown
            size={14}
            className={
              'shrink-0 text-[color:var(--color-ink-muted)] transition-transform ' +
              (open ? 'rotate-180' : '')
            }
          />
        </button>

        {open ? (
          <div
            role="listbox"
            className="absolute top-full left-0 mt-1 w-72 max-w-[calc(100vw-2rem)] z-50 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-[color:var(--color-rule)]">
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)] pointer-events-none"
                >
                  <Search size={15} />
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    setHighlightedIndex(0)
                  }}
                  onKeyDown={onKeyDown}
                  placeholder="Search country…"
                  className="fd-search-pill w-full h-9 pl-9 pr-4 rounded-full text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] outline-none"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {available.length === 0 ? (
                <div className="px-3 py-4 text-sm text-center text-[color:var(--color-ink-muted)]">
                  {query.trim() ? 'No countries found.' : 'All countries added.'}
                </div>
              ) : (
                available.map((c, i) => {
                  const RowFlag = Flags[c.code]
                  return (
                    <button
                      key={c.code}
                      ref={(el) => {
                        optionRefs.current[i] = el
                      }}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => add(c.code)}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      className={
                        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                        (i === highlightedIndex
                          ? 'bg-[color:var(--color-accent-bg)] ring-1 ring-inset ring-[color:var(--color-accent)]'
                          : '')
                      }
                    >
                      {RowFlag ? (
                        <RowFlag
                          title={c.name}
                          style={{ width: 20, height: 14, flexShrink: 0 }}
                        />
                      ) : (
                        <span className="w-5" />
                      )}
                      <span className="flex-1 truncate text-[color:var(--color-ink)]">
                        {c.name}
                      </span>
                      <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                        {c.code}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function defaultDraft() {
  return {
    geo: { enabled: false, blockedCountries: [] },
    ipRules: { enabled: false, allowedIps: '', deniedIps: '' },
  }
}

/** Split a textarea value (one entry per line) into a trimmed,
 *  blank-stripped array for the API payload. */
function linesToList(text) {
  return String(text || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

/** Join an API list back into newline-separated textarea text. */
function listToLines(list) {
  return Array.isArray(list) ? list.join('\n') : ''
}

/**
 * IP Access editor — embedded as a section of the Settings page.
 * Region lock plus a manual IP allow / deny list. Reads + writes the
 * cross-tenant `ip-access` setting via `/super-admin/platform/ip-access`;
 * owns its own Save button because its payload + validation are distinct
 * from the key/value settings above it.
 */
export function IpAccessSettings() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState(defaultDraft)
  const [serverSnapshot, setServerSnapshot] = useState(defaultDraft)

  const cfgQuery = useQuery({
    queryKey: ['platform-ip-access'],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/ip-access')
      return res.data
    },
  })

  useEffect(() => {
    const data = cfgQuery.data?.data
    if (!data) return
    const next = {
      geo: {
        enabled: Boolean(data.geo?.enabled),
        blockedCountries: Array.isArray(data.geo?.blockedCountries)
          ? data.geo.blockedCountries
          : [],
      },
      ipRules: {
        enabled: Boolean(data.ipRules?.enabled),
        allowedIps: listToLines(data.ipRules?.allowedIps),
        deniedIps: listToLines(data.ipRules?.deniedIps),
      },
    }
    setDraft(next)
    setServerSnapshot(next)
  }, [cfgQuery.data])

  const isDirty = useMemo(() => {
    const blockedKey = (g) => [...g.blockedCountries].sort().join(',')
    return (
      draft.geo.enabled !== serverSnapshot.geo.enabled ||
      blockedKey(draft.geo) !== blockedKey(serverSnapshot.geo) ||
      draft.ipRules.enabled !== serverSnapshot.ipRules.enabled ||
      draft.ipRules.allowedIps !== serverSnapshot.ipRules.allowedIps ||
      draft.ipRules.deniedIps !== serverSnapshot.ipRules.deniedIps
    )
  }, [draft, serverSnapshot])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        geo: {
          enabled: draft.geo.enabled,
          blockedCountries: draft.geo.blockedCountries,
        },
        ipRules: {
          enabled: draft.ipRules.enabled,
          allowedIps: linesToList(draft.ipRules.allowedIps),
          deniedIps: linesToList(draft.ipRules.deniedIps),
        },
      }
      const res = await api.put('/super-admin/platform/ip-access', payload)
      return res.data
    },
    onSuccess: () => {
      toast.success('IP access settings saved successfully.')
      void qc.invalidateQueries({ queryKey: ['platform-ip-access'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function patchGeo(patch) {
    setDraft((prev) => ({ ...prev, geo: { ...prev.geo, ...patch } }))
  }
  function patchRules(patch) {
    setDraft((prev) => ({ ...prev, ipRules: { ...prev.ipRules, ...patch } }))
  }

  return (
    <section>
      {/* Section header mirrors the page header above (title left, a
       *  scoped Save button right). The button saves only the IP access
       *  payload — the key/value settings have their own "Save all
       *  changes" button at the top of the page. */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
            <ShieldBan
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            IP Access
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Control which networks and regions can reach the platform.
          </p>
        </div>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || saveMutation.isPending}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save size={14} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {cfgQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : cfgQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(cfgQuery.error).message}
        </p>
      ) : (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          {/* Geographic blocker (§3) */}
          <div className="px-6 py-5 border-b border-[color:var(--color-rule)]">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-2 m-0">
                  <Globe
                    size={20}
                    className="text-[color:var(--color-accent-strong)]"
                  />
                  Region lock
                </h3>
                <p className="mt-1 text-sm text-[color:var(--color-ink-muted)] leading-relaxed">
                  Restrict each company to the country its plan was purchased
                  in. A company signing in from a different country is locked
                  out until they return. Super Admin created companies and demo
                  tenants are exempt, and the check is skipped whenever the
                  request country cannot be determined.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {draft.geo.enabled ? (
                  <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]">
                    On
                  </span>
                ) : (
                  <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                    Off
                  </span>
                )}
                <Toggle
                  checked={draft.geo.enabled}
                  onChange={(next) => patchGeo({ enabled: next })}
                  label="Toggle region lock"
                />
              </div>
            </div>

            {/* Platform-wide blocked-regions deny-list — applies on top of
                the per-company purchase-country lock above. */}
            <div className="mt-1">
              <label className="block text-sm font-semibold text-[color:var(--color-ink-strong)] mb-1">
                Blocked regions
              </label>
              <p className="text-sm text-[color:var(--color-ink-muted)] mb-2 leading-relaxed">
                Sign-ins detected from these countries are blocked for every
                company, on top of the purchase-country lock above. Leave empty
                to rely on the purchase-country lock alone. The check is skipped
                when the request country cannot be determined (including
                localhost), so it never affects local development.
              </p>
              <CountryMultiSelect
                value={draft.geo.blockedCountries}
                onChange={(next) => patchGeo({ blockedCountries: next })}
                disabled={!draft.geo.enabled}
              />
            </div>
          </div>

          {/* Manual allow / deny list (§3) */}
          <div className="px-6 py-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-2 m-0">
                  <ShieldBan
                    size={20}
                    className="text-[color:var(--color-accent-strong)]"
                  />
                  IP allow / deny list
                </h3>
                <p className="mt-1 text-sm text-[color:var(--color-ink-muted)] leading-relaxed">
                  Block individual networks or lock the whole platform down to a
                  trusted set. Enter one IP address or CIDR block per line (for
                  example 203.0.113.4 or 10.0.0.0/8). Denied addresses are always
                  blocked. When the allow list is not empty, only addresses on it
                  may connect.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {draft.ipRules.enabled ? (
                  <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]">
                    On
                  </span>
                ) : (
                  <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                    Off
                  </span>
                )}
                <Toggle
                  checked={draft.ipRules.enabled}
                  onChange={(next) => patchRules({ enabled: next })}
                  label="Toggle IP allow and deny list"
                />
              </div>
            </div>

            {draft.ipRules.enabled &&
            linesToList(draft.ipRules.allowedIps).length > 0 ? (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] px-3 py-2 text-sm text-[color:var(--color-warning)]">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  The allow list is active. Only the addresses below will be
                  able to reach the platform once you press Save. Make sure your
                  own network is included.
                </span>
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[color:var(--color-ink-strong)] mb-1">
                  Allow list
                </label>
                <textarea
                  rows={6}
                  value={draft.ipRules.allowedIps}
                  onChange={(e) => patchRules({ allowedIps: e.target.value })}
                  disabled={!draft.ipRules.enabled}
                  placeholder={'203.0.113.4\n10.0.0.0/8'}
                  className="w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm font-mono text-[color:var(--color-ink-strong)] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[color:var(--color-ink-strong)] mb-1">
                  Deny list
                </label>
                <textarea
                  rows={6}
                  value={draft.ipRules.deniedIps}
                  onChange={(e) => patchRules({ deniedIps: e.target.value })}
                  disabled={!draft.ipRules.enabled}
                  placeholder={'198.51.100.23\n192.0.2.0/24'}
                  className="w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm font-mono text-[color:var(--color-ink-strong)] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)]"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <SettingsUpdatedAt at={cfgQuery.data?.updatedAt} />
    </section>
  )
}
